import { useEffect, useState } from 'react';
import './HomePageModern.css';

const useCountUp = (target, duration = 1800, start = true) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf;
    const started = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - started) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setVal(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);
  return val;
};

const HomePageModern = ({ onNavigate, currentPage }) => {
  const fundedM = useCountUp(10, 1600);
  const liftPts = useCountUp(150, 2000);

  return (
    <div className="hpm">
      <div className="hpm-grain" aria-hidden="true"></div>
      <div className="hpm-mesh" aria-hidden="true">
        <div className="hpm-bloom hpm-bloom-1"></div>
        <div className="hpm-bloom hpm-bloom-2"></div>
        <div className="hpm-bloom hpm-bloom-3"></div>
      </div>

      <header className="hpm-header">
        <div className="hpm-header-inner">
          <button className="hpm-logo" onClick={() => onNavigate('home')} aria-label="Home">
            <img src="/creditrepairlogo.png" alt="Infinite Growth Consulting" />
          </button>
          <nav className="hpm-nav">
            <button
              className={`hpm-navlink ${currentPage === 'home' ? 'active' : ''}`}
              onClick={() => onNavigate('home')}
            >
              Home
            </button>
            <button
              className={`hpm-navlink ${currentPage === 'services' ? 'active' : ''}`}
              onClick={() => onNavigate('services')}
            >
              Services
            </button>
            <button className="hpm-nav-cta" onClick={() => onNavigate('services')}>
              Book Consultation
              <span className="hpm-arrow">→</span>
            </button>
          </nav>
        </div>
      </header>

      <section className="hpm-hero">
        <div className="hpm-particles" aria-hidden="true">
          <span className="hpm-particle hpm-p-1">+82</span>
          <span className="hpm-particle hpm-pcream hpm-p-2">780</span>
          <span className="hpm-particle hpm-p-3">+150</span>
          <span className="hpm-particle hpm-p-4">850</span>
          <span className="hpm-particle hpm-pcream hpm-p-5">+200</span>
          <span className="hpm-particle hpm-pmuted hpm-p-6">$</span>
          <span className="hpm-particle hpm-p-7">+120</span>
          <span className="hpm-particle hpm-pmuted hpm-p-8">◆</span>
          <span className="hpm-particle hpm-pcream hpm-p-9">720</span>
          <span className="hpm-particle hpm-p-10">+175</span>
          <span className="hpm-particle hpm-pmuted hpm-p-11">▲</span>
          <span className="hpm-particle hpm-p-12">800</span>
        </div>
        <div className="hpm-hero-eyebrow">
          <span className="hpm-dot"></span>
          Florida-Based · Nationwide Service
        </div>
        <div className="hpm-hero-grid">
          <div className="hpm-hero-left">
            <h1 className="hpm-hero-title">
              <span className="hpm-serif-italic">Unlock</span> Your
              <br />
              <span className="hpm-hero-green">Financial</span> <em className="hpm-hero-em">Potential</em>
            </h1>
            <p className="hpm-hero-sub">
              Credit repair, funding access, and real estate leverage, engineered for people who are done waiting on their future.
            </p>
            <div className="hpm-hero-ctas">
              <button className="hpm-btn-primary" onClick={() => onNavigate('services')}>
                Book Your Free Consultation
                <span className="hpm-arrow">→</span>
              </button>
              <a className="hpm-btn-ghost" href="#results">See Results</a>
            </div>
            <dl className="hpm-hero-stats">
              <div>
                <dt className="hpm-stat hpm-stat-green">${fundedM}M+</dt>
                <dd>Funded</dd>
              </div>
              <div>
                <dt className="hpm-stat hpm-stat-white">30-90</dt>
                <dd>Days</dd>
              </div>
              <div>
                <dt className="hpm-stat hpm-stat-gold">+{liftPts}</dt>
                <dd>Pts Avg. Lift</dd>
              </div>
            </dl>
          </div>
          <div className="hpm-hero-right">
            <div className="hpm-video-frame">
              <div className="hpm-video-corner hpm-tl"></div>
              <div className="hpm-video-corner hpm-tr"></div>
              <div className="hpm-video-corner hpm-bl"></div>
              <div className="hpm-video-corner hpm-br"></div>
              <video controls poster="/videos/preview.png" preload="metadata">
                <source src="/videos/montage.MP4" type="video/mp4" />
              </video>
            </div>
            <div className="hpm-video-meta">
              <span>Client testimonials</span>
              <span>·</span>
              <span>Real score moves</span>
            </div>
          </div>
        </div>
      </section>

      <div className="hpm-marquee" aria-hidden="true">
        <div className="hpm-marquee-track">
          {Array.from({ length: 2 }).map((_, i) => (
            <div className="hpm-marquee-group" key={i}>
              <span>+82 Points</span><span className="hpm-mdot">◆</span>
              <span>Collections Removed</span><span className="hpm-mdot">◆</span>
              <span>+150 Points</span><span className="hpm-mdot">◆</span>
              <span>Funding Approved</span><span className="hpm-mdot">◆</span>
              <span>+200 Points</span><span className="hpm-mdot">◆</span>
              <span>Late Payments Cleared</span><span className="hpm-mdot">◆</span>
              <span>+120 Points</span><span className="hpm-mdot">◆</span>
              <span>Bankruptcy Disputed</span><span className="hpm-mdot">◆</span>
              <span>+175 Points</span><span className="hpm-mdot">◆</span>
            </div>
          ))}
        </div>
      </div>

      <section className="hpm-services">
        <header className="hpm-section-header">
          <div className="hpm-label">01 · Services</div>
          <h2 className="hpm-section-title">
            A Complete System for <span className="hpm-serif-italic">Building Wealth</span>
          </h2>
          <p className="hpm-section-lede">
            Three pillars, engineered to compound. Fix the score, access the capital, move it into assets.
          </p>
        </header>

        <div className="hpm-services-grid">
          <article className="hpm-service-card">
            <div className="hpm-service-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <polyline points="3 17 9 11 13 15 21 7" />
                <polyline points="15 7 21 7 21 13" />
              </svg>
            </div>
            <h3>Professional Credit Repair</h3>
            <p className="hpm-service-sub">Clear negatives. Raise the score. Open the doors.</p>
            <ul>
              <li>Dispute collections, bankruptcies, inquiries, and late payments.</li>
              <li>Significant score improvement in 30 to 90 days.</li>
              <li>Personalized dispute strategy, not cookie-cutter letters.</li>
            </ul>
          </article>

          <article className="hpm-service-card hpm-service-card-emph">
            <div className="hpm-service-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M7 4c-1 1-1.5 2.5-1.5 4 0 3 2 5 5 5h2c1 0 2 0.5 2 2s-1 2-2 2H7" />
                <path d="M12 3v2" />
                <path d="M12 17v2" />
                <circle cx="12" cy="11" r="1" />
                <path d="M5 21c1.5-1 3.5-1.5 7-1.5s5.5 0.5 7 1.5" />
                <path d="M4 13c-1-1-1-2.5 0-3.5 1.5-1.5 4-1 5 0.5" />
              </svg>
            </div>
            <h3>Business Funding</h3>
            <p className="hpm-service-sub">Access $50K to $100K+ at 0% interest.</p>
            <ul>
              <li>Funding strategies tailored to your credit profile.</li>
              <li>Banking relationships built for rapid approvals.</li>
              <li>Ideal for entrepreneurs, startups, and expansion.</li>
            </ul>
          </article>

          <article className="hpm-service-card">
            <div className="hpm-service-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M3 11 L12 3 L21 11" />
                <path d="M5 10 L5 20 L10 20 L10 14 L14 14 L14 20 L19 20 L19 10" />
              </svg>
            </div>
            <h3>Real Estate Leverage</h3>
            <p className="hpm-service-sub">Turn credit into cash-flowing assets.</p>
            <ul>
              <li>Turn funding into profitable real estate deals.</li>
              <li>Mentorship from an experienced operator.</li>
              <li>Proven strategies to scale quickly.</li>
            </ul>
          </article>
        </div>
      </section>

      <section className="hpm-why">
        <header className="hpm-section-header">
          <div className="hpm-label">02 · Why Us</div>
          <h2 className="hpm-section-title">
            Built Different. <span className="hpm-serif-italic">On Purpose.</span>
          </h2>
        </header>
        <div className="hpm-why-grid">
          <div className="hpm-why-item">
            <span className="hpm-why-num">01</span>
            <h4>Proven Results</h4>
            <p>$10M+ successfully funded for clients nationwide.</p>
          </div>
          <div className="hpm-why-item">
            <span className="hpm-why-num">02</span>
            <h4>Expert Guidance</h4>
            <p>Personal mentorship from experienced professionals.</p>
          </div>
          <div className="hpm-why-item">
            <span className="hpm-why-num">03</span>
            <h4>Fast & Transparent</h4>
            <p>Clear plans, visible results, no mystery timelines.</p>
          </div>
          <div className="hpm-why-item">
            <span className="hpm-why-num">04</span>
            <h4>Community Focused</h4>
            <p>A network of growth-minded operators, not a 1-800 line.</p>
          </div>
        </div>
      </section>

      <section className="hpm-results" id="results">
        <header className="hpm-section-header">
          <div className="hpm-label">03 · Results</div>
          <h2 className="hpm-section-title">
            Real Score Moves. <span className="hpm-serif-italic">Real Clients.</span>
          </h2>
          <p className="hpm-section-lede">
            Not stock photos. Not curated flukes. The people below ran the process.
          </p>
        </header>

        <div className="hpm-bento">
          <div className="hpm-bento-cell">
            <img src="/images/portfolio/9C04A51F-9B0C-47DA-AC70-CF63E6978801.jpeg" alt="Credit score improvement" />
          </div>
          <div className="hpm-bento-cell">
            <img src="/images/portfolio/A5CD5E45-A044-410B-B482-04D834BD3916.png" alt="Credit score improvement" />
          </div>
          <div className="hpm-bento-cell">
            <img src="/images/portfolio/imgi_38_3e9f49e4-7a4b-4a7a-8de6-3c5a00de30dd.jpg" alt="Credit score improvement" />
          </div>
          <div className="hpm-bento-cell">
            <img src="/images/portfolio/imgi_65_5119f6ae-84ca-438a-b10d-4fee74a10913.jpg" alt="Credit score improvement" />
          </div>
          <div className="hpm-bento-cell">
            <img src="/images/portfolio/imgi_43_a814fa55-45cf-47d1-a824-d4c69e19ae0f.jpg" alt="Credit score improvement" />
          </div>
          <div className="hpm-bento-cell">
            <img src="/images/portfolio/imgi_66_f07ed06a-4d55-458c-9fc2-5c131279cbcf.jpg" alt="Credit score improvement" />
          </div>
          <div className="hpm-bento-cell">
            <img src="/images/portfolio/crditbumbp.jpg" alt="+109 point increase to 751" />
          </div>
          <div className="hpm-bento-cell">
            <img src="/images/portfolio/square pics/65DDAC1F-B52A-4AA8-9A2E-08771888BC35.jpeg" alt="Credit score improvement" />
          </div>
          <div className="hpm-bento-cell">
            <img src="/images/portfolio/square pics/imgi_24_bbac5a28-7ff0-48a1-9b77-af935142f728.jpg" alt="Credit score improvement" />
          </div>
        </div>

        <div className="hpm-results-stats">
          <div><strong>+150</strong><span>Average increase</span></div>
          <div><strong>90 days</strong><span>Average timeline</span></div>
          <div><strong>850</strong><span>Highest score</span></div>
        </div>
      </section>

      <section className="hpm-cta">
        <div className="hpm-cta-inner">
          <div className="hpm-label hpm-label-light">Your Move</div>
          <h2 className="hpm-cta-title">
            Ready to <span className="hpm-serif-italic">Accelerate</span><br />
            Your Financial Growth?
          </h2>
          <p className="hpm-cta-sub">Consultation is free. The cost of waiting is not.</p>
          <button className="hpm-btn-primary hpm-btn-large" onClick={() => onNavigate('services')}>
            View Our Services
            <span className="hpm-arrow">→</span>
          </button>
        </div>
      </section>
    </div>
  );
};

export default HomePageModern;
